import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Phone,
  Email,
  Note,
  Event,
  TrendingUp,
  Add,
  Edit,
  Delete,
} from '@mui/icons-material';
import { repAPI } from '../../services/api';

const LeadTimeline = ({ lead, onActivityAdded }) => {
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form states
  const [callForm, setCallForm] = useState({ description: '', outcome: '', followUpDate: '' });
  const [emailForm, setEmailForm] = useState({ description: '', subject: '' });
  const [meetingForm, setMeetingForm] = useState({ description: '', date: '', attendees: '' });
  const [noteForm, setNoteForm] = useState({ content: '', isInternal: false });
  const [editingNote, setEditingNote] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'CALL': return <Phone />;
      case 'EMAIL': return <Email />;
      case 'MEETING': return <Event />;
      case 'NOTE': return <Note />;
      case 'STATUS_CHANGE': return <TrendingUp />;
      default: return <Note />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'CALL': return '#2196f3';
      case 'EMAIL': return '#9c27b0';
      case 'MEETING': return '#ff9800';
      case 'NOTE': return '#4caf50';
      case 'STATUS_CHANGE': return '#f44336';
      default: return '#757575';
    }
  };

  const formatActivityType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Combine notes and activities, sort by date
  // Filter out redundant "Added note" activities since notes themselves show in timeline
  const timelineItems = React.useMemo(() => {
    const items = [];
    
    // Add activities (but filter out redundant "Added note" activities)
    if (lead.activities) {
      lead.activities.forEach(activity => {
        // Skip "Added note" activities - they're redundant with the actual notes
        if (activity.activityType === 'NOTE' && activity.description === 'Added note') {
          return;
        }
        items.push({
          type: 'activity',
          id: activity.id,
          activityType: activity.activityType,
          description: activity.description,
          createdAt: activity.createdAt,
          userId: activity.userId,
          metadata: activity.metadata ? (typeof activity.metadata === 'string' ? JSON.parse(activity.metadata) : activity.metadata) : null,
        });
      });
    }
    
    // Add notes
    if (lead.notes) {
      lead.notes.forEach(note => {
        items.push({
          type: 'note',
          id: note.id,
          content: note.content,
          isInternal: note.isInternal,
          createdAt: note.createdAt,
          userId: note.userId,
          updatedAt: note.updatedAt,
        });
      });
    }
    
    // Sort by date (newest first)
    return items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [lead]);

  const handleOpenActivityDialog = (type, existingActivity = null) => {
    setActivityType(type);
    setEditingActivity(existingActivity);
    setActivityDialogOpen(true);
    setError('');
    
    if (existingActivity) {
      // Populate forms with existing activity data
      const metadata = existingActivity.metadata || {};
      if (type === 'CALL') {
        setCallForm({
          description: existingActivity.description || '',
          outcome: metadata.outcome || '',
          followUpDate: metadata.followUpDate || '',
        });
      } else if (type === 'EMAIL') {
        setEmailForm({
          description: existingActivity.description || '',
          subject: metadata.subject || '',
        });
      } else if (type === 'MEETING') {
        setMeetingForm({
          description: existingActivity.description || '',
          date: metadata.date || '',
          attendees: metadata.attendees || '',
        });
      }
    } else {
      // Reset forms for new activity
      setCallForm({ description: '', outcome: '', followUpDate: '' });
      setEmailForm({ description: '', subject: '' });
      setMeetingForm({ description: '', date: '', attendees: '' });
    }
  };

  const handleOpenNoteDialog = () => {
    setNoteDialogOpen(true);
    setNoteForm({ content: '', isInternal: false });
    setError('');
  };

  const handleLogActivity = async () => {
    if (!activityType) return;

    let description = '';
    let metadata = null;

    switch (activityType) {
      case 'CALL':
        if (!callForm.description.trim()) {
          setError('Description is required');
          return;
        }
        description = callForm.description;
        metadata = {
          outcome: callForm.outcome || null,
          followUpDate: callForm.followUpDate || null,
        };
        break;
      case 'EMAIL':
        if (!emailForm.description.trim()) {
          setError('Description is required');
          return;
        }
        description = emailForm.description;
        metadata = {
          subject: emailForm.subject || null,
        };
        break;
      case 'MEETING':
        if (!meetingForm.description.trim()) {
          setError('Description is required');
          return;
        }
        description = meetingForm.description;
        metadata = {
          date: meetingForm.date || null,
          attendees: meetingForm.attendees || null,
        };
        break;
      default:
        return;
    }

    try {
      setLoading(true);
      setError('');
      
      if (editingActivity) {
        // Update existing activity
        await repAPI.updateLeadActivity(lead.id, editingActivity.id, activityType, description, metadata);
      } else {
        // Create new activity
        await repAPI.logLeadActivity(lead.id, activityType, description, metadata);
      }
      
      setActivityDialogOpen(false);
      setActivityType(null);
      setEditingActivity(null);
      onActivityAdded();
    } catch (err) {
      setError(err.message || (editingActivity ? 'Failed to update activity' : 'Failed to log activity'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteActivityConfirm = (activity) => {
    setActivityToDelete(activity);
    setDeleteConfirmOpen(true);
    setError('');
  };

  const handleDeleteActivity = async () => {
    if (!activityToDelete) return;

    try {
      setLoading(true);
      setError('');
      
      await repAPI.deleteLeadActivity(lead.id, activityToDelete.id);
      
      setDeleteConfirmOpen(false);
      setActivityToDelete(null);
      onActivityAdded();
    } catch (err) {
      setError(err.message || 'Failed to delete activity');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.content.trim()) {
      setError('Note content is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await repAPI.addLeadNote(lead.id, noteForm.content, noteForm.isInternal);
      
      setNoteDialogOpen(false);
      setNoteForm({ content: '', isInternal: false });
      onActivityAdded();
    } catch (err) {
      setError(err.message || 'Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({ content: note.content, isInternal: note.isInternal });
    setNoteDialogOpen(true);
    setError('');
  };

  const handleUpdateNote = async () => {
    if (!noteForm.content.trim()) {
      setError('Note content is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await repAPI.updateLeadNote(lead.id, editingNote.id, noteForm.content, noteForm.isInternal);
      
      setNoteDialogOpen(false);
      setEditingNote(null);
      setNoteForm({ content: '', isInternal: false });
      onActivityAdded();
    } catch (err) {
      setError(err.message || 'Failed to update note');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      setLoading(true);
      setError('');
      
      await repAPI.deleteLeadNote(lead.id, noteToDelete.id);
      
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
      onActivityAdded();
    } catch (err) {
      setError(err.message || 'Failed to delete note');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (note) => {
    setNoteToDelete(note);
    setDeleteConfirmOpen(true);
    setError('');
  };

  // Get current user ID for permission checks
  const getCurrentUserId = () => {
    try {
      const userStr = sessionStorage.getItem('barebiologics_currentUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id;
      }
    } catch (e) {
      console.error('Error getting current user:', e);
    }
    return null;
  };

  const currentUserId = getCurrentUserId();

  const renderActivityForm = () => {
    switch (activityType) {
      case 'CALL':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Call Description *"
              multiline
              rows={3}
              value={callForm.description}
              onChange={(e) => setCallForm({ ...callForm, description: e.target.value })}
              disabled={loading}
              required
            />
            <TextField
              label="Outcome"
              value={callForm.outcome}
              onChange={(e) => setCallForm({ ...callForm, outcome: e.target.value })}
              disabled={loading}
              placeholder="e.g., Left voicemail, Scheduled follow-up"
            />
            <TextField
              label="Follow-up Date"
              type="date"
              value={callForm.followUpDate}
              onChange={(e) => setCallForm({ ...callForm, followUpDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Box>
        );
      case 'EMAIL':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email Subject"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              disabled={loading}
            />
            <TextField
              label="Email Description *"
              multiline
              rows={4}
              value={emailForm.description}
              onChange={(e) => setEmailForm({ ...emailForm, description: e.target.value })}
              disabled={loading}
              required
              placeholder="Describe the email content or outcome"
            />
          </Box>
        );
      case 'MEETING':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Meeting Date"
              type="datetime-local"
              value={meetingForm.date}
              onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
            <TextField
              label="Attendees"
              value={meetingForm.attendees}
              onChange={(e) => setMeetingForm({ ...meetingForm, attendees: e.target.value })}
              disabled={loading}
              placeholder="Who attended the meeting?"
            />
            <TextField
              label="Meeting Description *"
              multiline
              rows={4}
              value={meetingForm.description}
              onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
              disabled={loading}
              required
              placeholder="Meeting notes and discussion points"
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            Activity Timeline
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Phone />}
            onClick={() => handleOpenActivityDialog('CALL')}
          >
            Log Call
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Email />}
            onClick={() => handleOpenActivityDialog('EMAIL')}
          >
            Log Email
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Event />}
            onClick={() => handleOpenActivityDialog('MEETING')}
          >
            Log Meeting
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Note />}
            onClick={handleOpenNoteDialog}
          >
            Add Note
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Timeline List */}
        {timelineItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
              No activity yet. Log calls, emails, or add notes to track your interactions.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {timelineItems.map((item, index) => (
              <React.Fragment key={item.id || index}>
                <ListItem 
                  alignItems="flex-start" 
                  sx={{ px: 0 }}
                  secondaryAction={
                    (item.type === 'note' && item.userId === currentUserId) ||
                    (item.type === 'activity' && 
                     item.userId === currentUserId && 
                     item.activityType !== 'STATUS_CHANGE' &&
                     ['CALL', 'EMAIL', 'MEETING'].includes(item.activityType)) ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            if (item.type === 'note') {
                              handleEditNote(item);
                            } else {
                              handleOpenActivityDialog(item.activityType, item);
                            }
                          }}
                          title={item.type === 'note' ? 'Edit note' : 'Edit activity'}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            if (item.type === 'note') {
                              handleOpenDeleteConfirm(item);
                            } else {
                              handleOpenDeleteActivityConfirm(item);
                            }
                          }}
                          title={item.type === 'note' ? 'Delete note' : 'Delete activity'}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: item.type === 'activity' ? getActivityColor(item.activityType) : '#4caf50',
                        width: 40,
                        height: 40,
                      }}
                    >
                      {item.type === 'activity' 
                        ? getActivityIcon(item.activityType)
                        : <Note />
                      }
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                          {item.type === 'activity' 
                            ? formatActivityType(item.activityType)
                            : item.isInternal ? 'Internal Note' : 'Note'
                          }
                        </Typography>
                        {item.isInternal && (
                          <Chip label="Internal" size="small" color="warning" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                          {item.type === 'activity' ? item.description : item.content}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                          {new Date(item.createdAt).toLocaleString()}
                          {item.type === 'note' && item.updatedAt && new Date(item.updatedAt).getTime() > new Date(item.createdAt).getTime() && (
                            <span> (edited)</span>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < timelineItems.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Activity Dialog */}
      <Dialog 
        open={activityDialogOpen} 
        onClose={() => {
          setActivityDialogOpen(false);
          setEditingActivity(null);
          setActivityType(null);
          setError('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingActivity ? 'Edit' : 'Log'} {activityType ? formatActivityType(activityType) : 'Activity'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {renderActivityForm()}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setActivityDialogOpen(false);
              setEditingActivity(null);
              setActivityType(null);
              setError('');
            }} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleLogActivity} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (editingActivity ? 'Update Activity' : 'Log Activity')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => {
        setNoteDialogOpen(false);
        setEditingNote(null);
        setNoteForm({ content: '', isInternal: false });
        setError('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingNote ? 'Edit Note' : 'Add Note'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Note Content *"
              multiline
              rows={5}
              value={noteForm.content}
              onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
              disabled={loading}
              required
              placeholder="Enter your note here..."
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="isInternal"
                checked={noteForm.isInternal}
                onChange={(e) => setNoteForm({ ...noteForm, isInternal: e.target.checked })}
                disabled={loading}
              />
              <label htmlFor="isInternal" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px' }}>
                Internal note (visible to admins only)
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNoteDialogOpen(false);
            setEditingNote(null);
            setNoteForm({ content: '', isInternal: false });
            setError('');
          }} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={editingNote ? handleUpdateNote : handleAddNote} 
            variant="contained" 
            disabled={loading || !noteForm.content.trim()}
          >
            {loading ? <CircularProgress size={20} /> : (editingNote ? 'Update Note' : 'Add Note')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => {
          setDeleteConfirmOpen(false);
          setNoteToDelete(null);
          setActivityToDelete(null);
          setError('');
        }} 
        maxWidth="xs" 
        fullWidth
      >
        <DialogTitle>
          Delete {noteToDelete ? 'Note' : activityToDelete ? formatActivityType(activityToDelete.activityType) : 'Item'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Are you sure you want to delete this {noteToDelete ? 'note' : activityToDelete ? formatActivityType(activityToDelete.activityType).toLowerCase() : 'item'}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setNoteToDelete(null);
              setActivityToDelete(null);
              setError('');
            }} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={noteToDelete ? handleDeleteNote : handleDeleteActivity} 
            variant="contained" 
            color="error" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeadTimeline;

